import React, { useState } from "react";
import { formatNumberWithCommas } from "../utils";
import "./style.css";

// Props interface for the UncontrolledQuantityInput component
interface UncontrolledQuantityInputProps {
  auctionId: string;
  isEditing: boolean;
  isDisabled: boolean;
  canSubmit: boolean;
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  isCancelling: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  maxCapacity: number;
  onStartEdit: () => void;
}

interface NonEditingViewState {
  displayValue: string;
  hasValue: boolean;
  isEditable: boolean;
  quantityValue: string;
  inputPrompt: string;
  validColor: string;
  borderColor: string;
  textColor: string;
}

const getNonEditingViewState = (
  initialValue: string,
  isDisabled: boolean,
  canSubmit: boolean,
  isValid: boolean
): NonEditingViewState => {
  const displayValue = initialValue ? formatNumberWithCommas(initialValue) : "";
  const hasValue = !!initialValue && initialValue.trim() !== "";
  const isEditable = !isDisabled && canSubmit;
  const quantityValue = isEditable ? "click to edit" : "";
  const inputPrompt = isEditable
    ? "Click to edit"
    : "Missing permissions required to return capacity";
  const validColor = hasValue ? "#d9d9d9" : "#6c757d";

  return {
    displayValue,
    hasValue,
    isEditable,
    quantityValue,
    inputPrompt,
    validColor,
    borderColor: isValid ? "#383C41" : "#dc3545",
    textColor: isValid ? validColor : "#dc3545",
  };
};

/**
 * A component for editing quantity inputs with validation
 * Uses an uncontrolled approach to maintain focus
 */
export const UncontrolledQuantityInput = React.memo(
  ({
    isEditing,
    isDisabled,
    canSubmit,
    initialValue,
    onSave,
    onCancel,
    inputRef,
    maxCapacity,
    onStartEdit,
  }: UncontrolledQuantityInputProps) => {
    // State to track validation errors
    const [validationError, setValidationError] = useState<string | null>(null);

    // Check if current value has validation issues
    const validateValue = (value: string): { isValid: boolean; error: string | null } => {
      if (!value || value.trim() === "") {
        return { isValid: true, error: null };
      }
      
      const numValue = parseFloat(value);
      
      if (isNaN(numValue)) {
        return { isValid: false, error: "Invalid number" };
      }
      
      if (numValue <= 0) {
        return { isValid: false, error: "Must be greater than zero" };
      }
      
      if (numValue > maxCapacity) {
        return { isValid: false, error: "Exceeds capacity" };
      }
      
      return { isValid: true, error: null };
    };

    // For editing mode, render an uncontrolled input
    if (isEditing) {
      return (
        <div style={{ width: "125px" }}>
          {validationError && (
            <div
              style={{
                color: "#dc3545",
                fontSize: "11px",
                marginBottom: "2px",
                position: "absolute",
                bottom: "100%",
                whiteSpace: "nowrap",
              }}
            >
              {validationError}
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            defaultValue={initialValue} // Use defaultValue for uncontrolled component
            placeholder="Enter quantity"
            onInput={(e) => {
              // Restrict input to digits, decimal point, and limit to 3 decimal places
              const target = e.target as HTMLInputElement;
              const value = target.value;

              // Only allow one decimal point and up to 3 decimal places
              const parts = value.split(".");
              if (parts.length > 2) {
                // More than one decimal point - keep only the first one
                target.value = parts[0] + "." + parts.slice(1).join("");
              } else if (parts.length === 2 && parts[1].length > 3) {
                // Too many decimal places - limit to 3
                target.value = parts[0] + "." + parts[1].substring(0, 3);
              } else {
                // Remove any non-digit and non-decimal point characters
                target.value = value.replace(/[^\d.]/g, "");
              }

              // Validate the current value
              const validation = validateValue(target.value);
              setValidationError(validation.error);
            }}
            onBlur={() => {
              // Don't save on blur - only validate input format
              // Save only when the user clicks the tick button or presses Enter
            }}
            onKeyDown={(e) => {
              // Allow only digits, decimal point, navigation keys, and control keys
              const allowedKeys = [
                "Enter",
                "Escape",
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Home",
                "End",
                ".",
              ];

              // Block any key that's not a digit, decimal point, or an allowed control key
              if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                e.preventDefault();
                return;
              }

              // Prevent multiple decimal points
              if (e.key === "." && e.currentTarget.value.includes(".")) {
                e.preventDefault();
                return;
              }

              if (e.key === "Enter") {
                const value = e.currentTarget.value;
                const validation = validateValue(value);

                if (value && value.trim() !== "" && validation.isValid) {
                  onSave(value);
                } else if (!validation.isValid) {
                  setValidationError(validation.error);
                } else {
                  onCancel();
                }
              } else if (e.key === "Escape") {
                onCancel();
              }
            }}
            style={{
              padding: "4px 8px",
              fontSize: "14px",
              borderRadius: "4px",
              border: `1px solid ${validationError ? "#dc3545" : "#383C41"}`,
              background: "#1e242b",
              color: validationError ? "#dc3545" : "#d9d9d9",
              width: "100%",
              height: "28px",
              boxSizing: "border-box",
              cursor: "text",
            }}
          />
        </div>
      );
    }

    // Check if the non-editing value has validation issues
    const validation = validateValue(initialValue);

    // For non-editing state, render a simple button with div like behavior with value or placeholder
    const {
      displayValue,
      hasValue,
      isEditable,
      quantityValue,
      inputPrompt,
      borderColor,
      textColor,
    } = getNonEditingViewState(initialValue, isDisabled, canSubmit, validation.isValid);

    return (
      <button
        type="button"
        className="quantity-input-button"
        disabled={!isEditable}
        aria-label={hasValue ? displayValue : inputPrompt}
        style={{
          border: `1px solid ${borderColor}`,
          background: isDisabled ? "#313439" : "#2a2e33",
          color: textColor,
          cursor: isEditable ? "pointer" : "not-allowed",
          justifyContent: hasValue ? "flex-start" : "center",
          fontStyle: hasValue ? "normal" : "italic",
        }}
        onClick={isEditable ? onStartEdit : undefined}
        title={isDisabled ? "Another row is being edited" : inputPrompt}
      >
        {hasValue ? displayValue : quantityValue}
      </button>
    );
  }
);